import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/d6t8jkdm9vk3c28i5dv0";

const contactInfo = [
  {
    icon: "ri-map-pin-line",
    label: "Address",
    value: "Cesta marsala Tita 51",
  },
  {
    icon: "ri-building-line",
    label: "City",
    value: "Jesenice",
  },
  {
    icon: "ri-mail-line",
    label: "Zip",
    value: "4270",
  },
  {
    icon: "ri-global-line",
    label: "Country",
    value: "Slovenia",
  },
  {
    icon: "ri-mail-send-line",
    label: "Email",
    value: "help@jekodoo.com",
  },
  {
    icon: "ri-phone-line",
    label: "Phone",
    value: "+1 8383323360",
  },
];

export default function ContactSection() {
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormState((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formState.message.length > 500) {
      setError("Message must be 500 characters or fewer.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const body = new URLSearchParams({
        name: formState.name,
        email: formState.email,
        subject: formState.subject,
        message: formState.message,
      });
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        setFormState({ name: "", email: "", subject: "", message: "" });
      } else {
        setError("Submission failed. Please try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-[#0d0d0d] py-32 px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-20">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="w-8 h-0.5 bg-emerald-400" />
            <span className="text-emerald-400 text-xs font-semibold tracking-widest uppercase">
              Contact
            </span>
            <span className="w-8 h-0.5 bg-emerald-400" />
          </div>
          <h2
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Get In Touch
          </h2>
          <p className="text-gray-400 text-base max-w-xl mx-auto leading-relaxed">
            Have a question about our products or want to learn more about
            JEKO, d.o.o.? We&apos;d love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl border border-white/5 bg-[#141414] p-8">
              <h3
                className="text-white font-semibold text-lg mb-6"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Contact Information
              </h3>
              <div className="space-y-5">
                {contactInfo.map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-400/10 border border-emerald-400/20 flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-emerald-400 text-sm`} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-widest mb-0.5">
                        {item.label}
                      </p>
                      <p className="text-white text-sm font-medium">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Company badge */}
            <div className="rounded-2xl border border-emerald-500/20 bg-emerald-900/10 p-6">
              <div className="flex items-center gap-3 mb-3">
                <img
                  src="https://public.readdy.ai/ai/img_res/377371b4-550c-49ea-87b3-851d396b54a6.png"
                  alt="JEKO Logo"
                  className="w-8 h-8 object-contain"
                />
                <span className="text-white font-semibold text-sm">JEKO, d.o.o.</span>
              </div>
              <p className="text-gray-400 text-xs leading-relaxed">
                Registered company in Slovenia. Developing modern iOS
                applications focused on user privacy and device performance.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-white/5 bg-[#141414] p-8">
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-400/10 border border-emerald-400/30 mb-6">
                    <i className="ri-check-line text-emerald-400 text-2xl" />
                  </div>
                  <h3 className="text-white font-semibold text-xl mb-2">
                    Message Sent!
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Thank you for reaching out. We&apos;ll get back to you shortly.
                  </p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="jeko-contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-widest mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formState.name}
                        onChange={handleChange}
                        required
                        placeholder="Your name"
                        className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500/50 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-widest mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formState.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500/50 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-widest mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formState.subject}
                      onChange={handleChange}
                      required
                      placeholder="What is this regarding?"
                      className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500/50 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 uppercase tracking-widest mb-2">
                      Message{" "}
                      <span className="text-gray-600 normal-case tracking-normal">
                        ({formState.message.length}/500)
                      </span>
                    </label>
                    <textarea
                      name="message"
                      value={formState.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="Tell us how we can help..."
                      className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500/50 transition-colors resize-none"
                    />
                    {formState.message.length > 500 && (
                      <p className="text-red-400 text-xs mt-1">
                        Message cannot exceed 500 characters.
                      </p>
                    )}
                  </div>
                  {error && (
                    <p className="text-red-400 text-sm">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting || formState.message.length > 500}
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-sm py-3.5 rounded-xl transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
